import { useState, useEffect, useCallback } from "react"
import { useAuth } from "./useAuth"
import { VOICES, DEFAULT_VOICE_ID } from "../voice/voices"
import { VALID_VOICE_IDS } from "../../shared/voice-ids.js"

const STORAGE_KEY = "muse-settings"

export interface LocalPreferences {
  reducedMotion: boolean
  visualOrb: boolean
  constellationTouch: "off" | "gentle" | "full"
  voiceEnabled: boolean
  voiceId: string
  autoSpeak: boolean
  voiceMode: boolean
  playbackSpeed: number
  ttsMode: "browser" | "server"
  showReasoning: boolean
  readReasoningAloud: boolean
}

const DEFAULTS: LocalPreferences = {
  reducedMotion: false,
  visualOrb: false,
  constellationTouch: "off",
  voiceEnabled: false,
  voiceId: DEFAULT_VOICE_ID,
  autoSpeak: false,
  voiceMode: false,
  playbackSpeed: 1.0,
  ttsMode: "server",
  showReasoning: true,
  readReasoningAloud: false,
}

function readFromStorage(): LocalPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULTS }
    const parsed = JSON.parse(raw) as Partial<LocalPreferences>
    return {
      reducedMotion: parsed.reducedMotion ?? DEFAULTS.reducedMotion,
      visualOrb: parsed.visualOrb ?? DEFAULTS.visualOrb,
      constellationTouch: parsed.constellationTouch === "off" || parsed.constellationTouch === "gentle" || parsed.constellationTouch === "full"
        ? parsed.constellationTouch
        : DEFAULTS.constellationTouch,
      voiceEnabled: parsed.voiceEnabled ?? DEFAULTS.voiceEnabled,
      voiceId: typeof parsed.voiceId === "string" && VALID_VOICE_IDS.has(parsed.voiceId) ? parsed.voiceId : DEFAULT_VOICE_ID,
      autoSpeak: parsed.autoSpeak ?? DEFAULTS.autoSpeak,
      playbackSpeed: typeof parsed.playbackSpeed === "number" ? parsed.playbackSpeed : DEFAULTS.playbackSpeed,
      // voiceMode is transient UI state — never restore from storage.
      // If it was persisted true (e.g. crash mid-session), a refresh must
      // land in chat view, not stuck in voice mode with no visible exit.
      voiceMode: false,
      ttsMode: parsed.ttsMode === "browser" || parsed.ttsMode === "server"
        ? parsed.ttsMode
        : DEFAULTS.ttsMode,
      showReasoning: typeof parsed.showReasoning === "boolean" ? parsed.showReasoning : DEFAULTS.showReasoning,
      readReasoningAloud: typeof parsed.readReasoningAloud === "boolean" ? parsed.readReasoningAloud : DEFAULTS.readReasoningAloud,
    }
  } catch {
    return { ...DEFAULTS }
  }
}

export function useSettings() {
  const { refetch } = useAuth()
  const [prefs, setPrefs] = useState<LocalPreferences>(readFromStorage)

  // Keep localStorage in sync whenever prefs change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
    } catch {
      // localStorage unavailable (private browsing, quota exceeded) — silently ignore
    }
  }, [prefs])

  const updatePref = useCallback(<K extends keyof LocalPreferences>(
    key: K,
    value: LocalPreferences[K],
  ) => {
    // Validate voiceId against allowlist on write, not just read
    if (key === "voiceId" && !VALID_VOICE_IDS.has(value as string)) return
    setPrefs((prev) => ({ ...prev, [key]: value }))
  }, [])

  const updateServerPref = useCallback(async (patch: { default_model?: string | null; default_thinking?: boolean }) => {
    try {
      const res = await fetch("/api/user/preferences", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      })
      if (res.ok) {
        await refetch()
      }
    } catch {
      // Preferences are non-critical — silently ignore network errors
    }
  }, [refetch])

  return { prefs, updatePref, updateServerPref }
}
